const STORAGE_KEY = 'pagehopReferral';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long a /refer/:code link stays available for signup attribution on this browser. Matches the
 * 90-day lifetime used for marketing referral sources (see webStorageHelpers.js).
 */
export const PAGEHOP_REFERRAL_ATTRIBUTION_DAYS = 90;
const ATTRIBUTION_WINDOW_MS = PAGEHOP_REFERRAL_ATTRIBUTION_DAYS * DAY_MS;
const CODE_PATTERN = /^[A-HJ-NP-Z2-9]{8}$/;

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

/**
 * Normalizes a PageHop Rewards referral code (8 unambiguous uppercase characters).
 *
 * @param {string} value
 * @returns {string|null} normalized code or null when the value is not a valid code
 */
export const normalizePagehopReferralCode = value => {
  const code = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return CODE_PATTERN.test(code) ? code : null;
};

/**
 * Remembers a referral code from a /refer/:code link for a later signup on this browser.
 * The window is fixed when a code is first stored: reopening the same link does not extend it, and
 * other pages never touch it. A different valid code replaces the stored one (most recent link
 * wins) with its own window. Invalid codes are ignored.
 *
 * @param {string} code
 * @param {number} now current time in milliseconds
 * @returns {boolean} true when the code is stored (or already stored and unexpired)
 */
export const storePagehopReferralCode = (code, now = Date.now()) => {
  const normalized = normalizePagehopReferralCode(code);
  const store = storage();
  if (!normalized || !store) return false;
  try {
    if (getStoredPagehopReferralCode(now) === normalized) return true;
    store.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: normalized, expiresAt: now + ATTRIBUTION_WINDOW_MS })
    );
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * @returns {string|null} stored, unexpired referral code
 */
export const getStoredPagehopReferralCode = (now = Date.now()) => {
  const store = storage();
  if (!store) return null;
  try {
    const stored = JSON.parse(store.getItem(STORAGE_KEY) || 'null');
    if (!stored || !(stored.expiresAt > now)) return null;
    return normalizePagehopReferralCode(stored.code);
  } catch (e) {
    return null;
  }
};

export const clearStoredPagehopReferralCode = () => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch (e) {
    // Storage unavailable; nothing to clear.
  }
};

/**
 * Adds the stored referral code to signup extended data as private data. The server verifies the
 * code and decides eligibility; the client never asserts who referred whom.
 *
 * @param {Object} extendedData signup extended data (may be an empty object)
 * @returns {Object}
 */
export const withPagehopReferralCode = extendedData => {
  const code = getStoredPagehopReferralCode();
  if (!code) return extendedData;
  return {
    ...extendedData,
    privateData: { ...(extendedData?.privateData || {}), pagehopReferralCode: code },
  };
};
